/**
 * Slash-command grammar for the chat overlay. Pure and dependency-free (no DOM,
 * no i18n) so the parsing rules can be unit-tested on their own.
 *
 *   hello there        → say
 *   /w Min-jun hi      → whisper to Min-jun
 *   /w "Two Words" hi  → whisper to a nickname containing spaces
 *   /w Min-jun         → open a whisper tab with Min-jun (nothing sent yet)
 *   /r  sure           → reply to the last whisper partner
 *   /help              → usage
 */
export type ChatCommand =
  | { kind: 'say'; text: string }
  | { kind: 'whisper'; to: string; text: string }
  | { kind: 'reply'; text: string }
  | { kind: 'help' }
  | { kind: 'unknown'; name: string };

const WHISPER = new Set(['w', 'whisper', 'msg', 'dm', 'tell']);
const REPLY = new Set(['r', 'reply']);
const HELP = new Set(['help', 'h', '?']);

/** Parse one typed line. Returns null for an empty line (nothing to do). */
export function parseChatInput(raw: string): ChatCommand | null {
  const line = raw.trim();
  if (!line) return null;
  if (!line.startsWith('/')) return { kind: 'say', text: line };

  const name = /^\/(\S*)/.exec(line)![1].toLowerCase();
  const rest = line.slice(1 + name.length);

  if (WHISPER.has(name)) {
    const target = takeName(rest);
    if (!target) return { kind: 'help' }; // "/w" with no nickname → show the usage
    return { kind: 'whisper', to: target.name, text: target.rest.trim() };
  }
  if (REPLY.has(name)) return { kind: 'reply', text: rest.trim() };
  if (HELP.has(name)) return { kind: 'help' };
  return { kind: 'unknown', name };
}

/**
 * Pull the target nickname off the front of a whisper's arguments: either a
 * quoted "two word name" or a single whitespace-delimited token.
 */
function takeName(s: string): { name: string; rest: string } | null {
  const t = s.replace(/^\s+/, '');
  if (!t) return null;
  if (t[0] === '"' || t[0] === "'") {
    const end = t.indexOf(t[0], 1);
    if (end > 1) return { name: t.slice(1, end), rest: t.slice(end + 1) };
  }
  const sp = t.search(/\s/);
  return sp < 0 ? { name: t, rest: '' } : { name: t.slice(0, sp), rest: t.slice(sp) };
}
